import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { isFeatureEnabled } from "app/configs/featureFlags";

const WIDGET_BASE_URL = process.env.REACT_APP_CHATBOT_WIDGET_URL;
const THEME_OVERRIDE_URL = `${process.env.PUBLIC_URL}/chatbotTheme.css`;
// Absolute: the widget only treats an http(s) or data: value as an image; anything else is shown as text.
const BOT_ICON_URL = `${window.location.origin}${process.env.PUBLIC_URL}/chatbot/bot.svg`;
const API_BASE_URL =
  (process.env.REACT_APP_API_URL || "https://staging.ccticketing.eu/ticketingApi/") +
  "chatbot";

const BOT_NAME = "Ψηφιακός Βοηθός Εκπροσώπου";
// Pinned under the widget's header for the whole session, one paragraph per line, while the
// AI_CHATBOT_DISCLAIMER flag is on (REACT_APP_CHATBOT_DISCLAIMER_ENABLED=false removes it)
// (widget ≥ 0.4.4; older ones ignore it). In full until the first question, then folded to
// DISCLAIMER_SUMMARY (widget ≥ 0.4.5), which expands it again. The welcome below is bold
// via chatbotTheme.css.
const DISCLAIMER = [
  "Καλώς ήρθατε στον Ψηφιακό Βοηθό Εκπροσώπου.",
  "Ο Ψηφιακός Βοηθός βασίζεται στην Τεχνητή Νοημοσύνη (AI) και βρίσκεται σε δοκιμαστική (beta) λειτουργία.",
  "Οι απαντήσεις παράγονται αυτόματα, παρέχονται υποστηρικτικά και πρέπει να επαληθεύονται από τον εκπρόσωπο " +
    "πριν χρησιμοποιηθούν στην εξυπηρέτηση πολιτών. Ενδέχεται να υπάρχουν καθυστερήσεις στην απόκριση και " +
    "ανακρίβειες στις απαντήσεις.",
  "Παρακαλείστε να μην εισάγετε προσωπικά δεδομένα πολιτών, όπως ΑΦΜ, ΑΜΚΑ, ονοματεπώνυμο ή διεύθυνση.",
].join("\n");
const DISCLAIMER_SUMMARY = "AI σε beta · Όχι προσωπικά δεδομένα";
const WELCOME = "Πώς μπορώ να σας βοηθήσω σήμερα;";

function loadStylesheet(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

const ChatbotBubble = () => {
  const chatbotRef = useRef(null);
  const user = useSelector((state) => state.user);
  // Deterministic per-user session id: the widget stores conversation history in this
  // browser's localStorage under this id, so each user on a shared machine gets their own
  // conversation back. It does not follow them to another browser or device.
  const sessionId = user?.id ? `user-${user.id}` : undefined;

  useEffect(() => {
    if (!WIDGET_BASE_URL || WIDGET_BASE_URL === "REPLACE_ME_CHATBOT_WIDGET_BASE_URL") {
      console.error(
        "REACT_APP_CHATBOT_WIDGET_URL is not configured — the chatbot widget will not load."
      );
      return;
    }

    let cancelled = false;

    loadStylesheet(`${WIDGET_BASE_URL}/style.css`);
    loadStylesheet(THEME_OVERRIDE_URL);

    loadScript(`${WIDGET_BASE_URL}/chatbot.umd.js`).then(() => {
      if (cancelled || !window.AIChatbot) return;
      chatbotRef.current = new window.AIChatbot({
        apiUrl: API_BASE_URL,
        getAuthToken: () => localStorage.getItem("jwt_access_token"),
        theme: "light",
        sessionId,
        botName: BOT_NAME,
        disclaimer: isFeatureEnabled("AI_CHATBOT_DISCLAIMER") ? DISCLAIMER : "",
        disclaimerSummary: DISCLAIMER_SUMMARY,
        welcomeMessage: WELCOME,
        welcomeIcon: "",
        botIcon: BOT_ICON_URL,
        getUserDetails: () => {
          const stored = JSON.parse(localStorage.getItem("user") || "{}");
          return {
            id: stored.id,
            username: stored.username,
            email: stored.email,
            firstName: stored.firstName,
            lastName: stored.lastName,
          };
        },
      });
      chatbotRef.current.init();
    });

    return () => {
      cancelled = true;
      chatbotRef.current?.destroy();
      chatbotRef.current = null;
    };
  }, [sessionId]);

  return null;
};

export default ChatbotBubble;
